export const MIN_HISTORY_COUNT = 3;
const NEUTRAL_BAND_PCT = 5;

export const extractSpxRef = (row) => {
  if (!row) return null;
  const candidates = [
    row.calendar_spread?.legs?.[0]?.stockprice,
    row.diagonal_spread?.legs?.[0]?.stockprice,
    row.vertical_spread?.legs?.[0]?.stockprice,
  ];
  for (const price of candidates) {
    const num = typeof price === 'number' ? price : Number(price);
    if (Number.isFinite(num) && num > 0) return num;
  }
  return null;
};

export const computeTotalDebit = (row) => {
  if (!row) return null;
  const calQty = row.calendar_quantity ? Number(row.calendar_quantity) : 1;
  const diagQty = row.diagonal_quantity ? Number(row.diagonal_quantity) : 1;
  const calCost = (row.calendar_spread?.cost ?? 0) * calQty;
  const diagCost = (row.diagonal_spread?.cost ?? 0) * diagQty;
  const vertCost = row.vertical_spread?.cost ?? 0;
  const total = calCost + diagCost + vertCost;
  return Number.isFinite(total) ? total : null;
};

export const computeNormalizedCost = (row) => {
  const spxRef = extractSpxRef(row);
  const totalDebit = computeTotalDebit(row);
  if (!Number.isFinite(spxRef) || spxRef <= 0 || !Number.isFinite(totalDebit)) {
    return null;
  }
  return totalDebit / spxRef;
};

export const buildCurrentEntry = (row) => {
  const totalDebit = computeTotalDebit(row);
  const spxRef = extractSpxRef(row);
  const normalizedCost = computeNormalizedCost(row);
  if (normalizedCost === null) return null;
  return { total_debit: totalDebit, spx_ref: spxRef, normalized_cost: normalizedCost };
};

export const computePctVsMedian = (currentNormalized, median) => {
  if (
    !Number.isFinite(currentNormalized) ||
    !Number.isFinite(median) ||
    median <= 0
  ) {
    return null;
  }
  return Math.round(((currentNormalized / median) - 1) * 1000) / 10;
};

export const formatPctVsMedian = (pct) => {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return '—';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct}%`;
};

export const getCostIndexColor = (pctVsMedian) => {
  if (pctVsMedian === null || pctVsMedian === undefined || !Number.isFinite(pctVsMedian)) {
    return 'var(--muted)';
  }
  if (pctVsMedian <= -NEUTRAL_BAND_PCT) return 'var(--positive, #16a34a)';
  if (pctVsMedian >= NEUTRAL_BAND_PCT) return 'var(--negative, #dc2626)';
  return 'var(--text)';
};

export const getCostIndexTooltip = (costIndex) => {
  if (!costIndex?.has_sufficient_history) {
    return 'Insufficient similar history (need ≥3 scans with same DTE, short deltas, vertical width, qty)';
  }
  const pct = costIndex.pct_vs_median;
  const percentile = costIndex.percentile;
  const cheaperPct = Number.isFinite(percentile) ? Math.round(100 - percentile) : null;
  const pctLabel = formatPctVsMedian(pct);
  if (cheaperPct !== null) {
    return `${pctLabel} vs historical median — cheaper than ${cheaperPct}% of past snapshots. Click for chart.`;
  }
  return `${pctLabel} vs historical median — click for chart.`;
};

export const normalizedCostToBps = (normalizedCost) => {
  if (!Number.isFinite(normalizedCost)) return null;
  return normalizedCost * 10000;
};

export const formatNormalizedCostPct = (normalizedCost) => {
  if (!Number.isFinite(normalizedCost)) return '—';
  return `${(normalizedCost * 100).toFixed(3)}% of SPX`;
};
